import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { MultipleOptionsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../../../../constants/errors';
import { ZodError } from 'zod';
import {
  BOLLARD_LIT,
  BOLLARD_UNLIT,
  SIGN_LIT,
  SIGN_UNLIT,
  STREET_NAME_SIGN_UNLIT,
} from '../../../../constants/signsBollards';
import { IssueType, useSignsBollardsStore } from '../../../../hooks';

export const IssueSelection: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    update,
    data: { issueType },
  } = useSignsBollardsStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Which of these does the issue relate to?';
  const errorPath = SELECT_OPTIONS_ERROR.required.path;

  const options = [
    {
      id: BOLLARD_UNLIT,
      title: BOLLARD_UNLIT,
      labelText: 'Bollard (unlit)',
      isDefault: issueType === BOLLARD_UNLIT,
      isLabelBold: false,
    },
    {
      id: BOLLARD_LIT,
      title: BOLLARD_LIT,
      labelText: 'Bollard with a light',
      isDefault: issueType === BOLLARD_LIT,
      isLabelBold: false,
    },
    {
      id: SIGN_UNLIT,
      title: SIGN_UNLIT,
      labelText: 'Sign (unlit)',
      isDefault: issueType === SIGN_UNLIT,
      isLabelBold: false,
    },
    {
      id: SIGN_LIT,
      title: SIGN_LIT,
      labelText: 'Sign with a light',
      isDefault: issueType === SIGN_LIT,
      isLabelBold: false,
    },
    {
      id: STREET_NAME_SIGN_UNLIT,
      title: STREET_NAME_SIGN_UNLIT,
      labelText: 'Street name sign (unlit)',
      isDefault: issueType === STREET_NAME_SIGN_UNLIT,
      isLabelBold: false,
    },
  ];

  const handleChange = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as string;

    if (updatedSelection) {
      update('issueType', updatedSelection as IssueType);
    } else {
      update('issueType', '');
    }
  };

  const radioOptions = [
    {
      onChange: handleChange,
      options: options,
      title: '',
    },
  ];

  const handleNavigate = () => {
    switch (issueType) {
      case BOLLARD_UNLIT:
        navigate(RoutesList.DESCRIBE_ISSUE);
        break;
      case BOLLARD_LIT:
        navigate(RoutesList.REPORT_ISSUE_TO_STREETLIGHTS_TEAM);
        break;
      case SIGN_UNLIT:
        navigate(RoutesList.DESCRIBE_ISSUE);
        break;
      case SIGN_LIT:
        navigate(RoutesList.REPORT_ISSUE_TO_STREETLIGHTS_TEAM);
        break;
      case STREET_NAME_SIGN_UNLIT:
        navigate(RoutesList.DESCRIBE_ISSUE);
        break;
      default:
        return null;
    }
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.SIGNS_BOLLARDS_HOME);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'issueType',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          issueType,
        });
        handleNavigate();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            message: SELECT_OPTIONS_ERROR.required.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <MultipleOptionsTemplate
      title={title}
      radioOptions={radioOptions}
      errorPath={errorPath}
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      onChange={handleChange}
    />
  );
};
